<template>
    <div v-if="viewNavBar()" class="side-nav-bar">
        <div class="nav-button" @click="navigateToChallengeFeedView">
            <img :src="logo" alt="Duelly Logo" class="logo-img" />
        </div>

        <!-- Desktop inline search -->
        <SearchInput
            placeholder="Search tags or keywords… ( / )"
            @open-sheet="
                (p) => {
                    prefill.q = p.q ?? '';
                    sheetOpen = true;
                }
            "
        />

        <SearchSheet
            v-model:open="sheetOpen"
            :initialQuery="prefill.q"
            @close="sheetOpen = false"
            @select-term="goResults"
        />

        <hr class="mt=-2" />

        <div class="nav-button" @click="navigateToChallengeFeedView">
            <span style="font-size: 28px !important">🎯</span>
            <h2>Challenges</h2>
        </div>

        <div
            class="nav-button"
            @click="$router.push({ path: '/create-content', query: { type: 'challenge' } })"
        >
            <span style="font-size: 28px !important">✨</span>
            <h2>Upload</h2>
        </div>

        <div class="nav-button" @click="$router.push('/notifications')">
            <span style="font-size: 28px !important">🔔</span>
            <h2>Notifications</h2>
        </div>

        <div class="nav-button"></div>

        <div v-if="isLoggedIn" class="nav-button" @click="navigateTo($router, '/user-management')">
            <ProfilePhoto
                id="step3"
                class="mr-2"
                :username="userProfile.username"
                :profile_photo="userProfile.profile_photo"
                size="small"
            />
            <h2>Profile</h2>
        </div>

        <div v-else class="nav-button">
            <a @click="duellyEventEmitter.emit('show-login-overlay')"><h2>Login</h2></a>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import logo from '@/assets/duelly_top_nav_log.svg';
import ProfilePhoto from '@/components/ProfilePhoto.vue';
import { useAuthStore } from '@/store/authStore';

import { navigateTo } from './utils.ts';
import { duellyEventEmitter } from '@/services/eventBus';
import SearchInput from '@/components/search/SearchInput.vue';
import SearchSheet from '@/components/search/SearchSheet.vue';

type SearchType = 'tag' | 'key'; // results combine both; we still accept from sheet

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const userProfile = ref(authStore.getUserProfile());
const sheetOpen = ref(false);
const prefill = ref<{ q: string }>({ q: '' });

const isLoggedIn = computed(() => authStore.ensureAuthTokens());

function viewNavBar(): boolean {
    return !(route.path === '/login' || route.path === '/' || route.path.startsWith('/admin'));
}

// SearchSheet selects a term; we always route to combined results
function goResults({ type, term }: { type: 'tag' | 'key'; term: string }) {
    const q = term.trim().toLowerCase();
    if (!q) return;
    sheetOpen.value = false; // close the sheet so focus returns to page
    router.push({
        path: '/search/results',
        query: { q, type }, // <- forward the kind
    });
}

function navigateToChallengeFeedView() {
    window.location.href = '/post-feed-view?type=challenge&reset';
}
</script>

<style scoped lang="scss">
@use '@/assets/scss/abstracts/variables' as *;

.side-nav-bar {
    display: none;
    border-right: 1px solid $color-primary;

    @media (min-width: $desktop-max-width) {
        position: fixed;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        top: 0;
        inset: 0;
        width: 100%;
        max-width: 300px;
        z-index: 100;
        padding: 8px 16px;
        gap: 20px;
        padding-right: 15px;
        background: $surface-primary;
    }
}
hr {
    border: 0;
    height: 1px;
    background: $color-primary;
    margin: 0;
}
.nav-button {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    gap: 10px;
    width: 100%;
}
.nav-button:active {
    opacity: 0.5;
}
.nav-button:hover {
    background-color: $color-duelly-fog;
}

.upload-icon {
    width: 48px;
    height: 48px;
    cursor: pointer;
}

.nav-logo {
    margin-top: 8px;
    margin-bottom: 16px;
}

.logo-img {
    height: 150px;
    max-height: 250px;
    max-width: 100%;
    cursor: pointer;
}
</style>
